import { ChangeEvent } from "react";
import { ReactComponent as ChevronDown } from "../../../resources/icons/chevron-down.svg";
import { Button } from "../buttons/Button";
import { FormFilter } from "../../../core/models/FormFilter";
import { nameOf } from "../../../core/functions/nameof";
import { FstyrDateRangePicker } from "../date-picker/DatePicker";
import { FstyrMultiSelect } from "../select/Select";
import { FormErrorType } from "../../../core/entities/FormErrorType";
import { StatusCodeType } from "../../../core/functions/GetFormErrorTypes";
import { Municipality } from "../../../core/entities/municipality";

export enum FilterComponentState {
    invalid,
    valid
}

export interface FormFilter_VM {
    componentState: FilterComponentState;
    errorTypes: FormErrorType[];
    statusCodes: StatusCodeType[];
    municipalities: Municipality[];
    onFormChange: () => void;
    filterFormModel: FormFilter;
    handleFilterChange: (filter: string, value: any) => void;
    handleFilterReset: () => void;
    isOpen: boolean;
    setOpenState: (open: boolean) => void;
    extendedFilters: boolean;
}

const allErrorTypes = "all-error-types";

export function FormFilterComponent({
    componentState,
    errorTypes,
    statusCodes,
    filterFormModel: filterForm,
    onFormChange: handleFormSubmission,
    handleFilterChange,
    handleFilterReset,
    isOpen,
    setOpenState,
    extendedFilters,
    municipalities
}: FormFilter_VM) {
    function onChange(e: ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        const name = e.target.name;
        handleFilterChange(name, value);
    }

    function onChangePeriod(period?: FormFilter["period"]) {
        const name = nameOf<FormFilter>((x) => x.period);
        handleFilterChange(name, period);
    }

    function onChangeErrorCode(errorType: string) {
        const onlyFormsWithErrorName = nameOf<FormFilter>(
            (x) => x.onlyFormsWithError
        );
        if (errorType === allErrorTypes) {
            handleFilterChange(
                onlyFormsWithErrorName,
                !filterForm.onlyFormsWithError
            );
            const errorTypesName = nameOf<FormFilter>((x) => x.errorTypes);
            handleFilterChange(errorTypesName, []);
            return;
        }

        handleFilterChange(onlyFormsWithErrorName, false);
        const name = nameOf<FormFilter>((x) => x.errorTypes);
        const errorTypes = filterForm.errorTypes;

        if (errorTypes.includes(errorType)) {
            handleFilterChange(
                name,
                errorTypes.filter((it) => it !== errorType)
            );
        } else {
            handleFilterChange(name, [...errorTypes, errorType]);
        }
    }

    function onChangeStatusCode(status: string) {
        const name = nameOf<FormFilter>((x) => x.statusTypes);
        const statusTypes = filterForm.statusTypes;

        if (statusTypes.includes(status)) {
            handleFilterChange(
                name,
                statusTypes.filter((it) => it !== status)
            );
        } else {
            handleFilterChange(name, [...statusTypes, status]);
        }
    }

    function onChangeMunicipality(municipality: string) {
        const name = nameOf<FormFilter>((x) => x.municipalities);
        const municipalities = filterForm.municipalities;

        if (municipalities.includes(municipality)) {
            handleFilterChange(
                name,
                municipalities.filter((it) => it !== municipality)
            );
        } else {
            handleFilterChange(name, [...municipalities, municipality]);
        }
    }

    function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
        if (event.key === "Enter") {
            handleFormSubmission();
        }
    }

    return (
        <div className={"px-3"}>
            <div className="flex flex-wrap">
                <div className="w-full md:w-1/3 mb-6 md:mb-0">
                    <label
                        className="block tracking-wide text-secondary-gray-dark text-sx font-medium mb-2"
                        htmlFor="grid-ssn"
                    >
                        Cpr-nummer
                    </label>
                    <input
                        id="grid-ssn"
                        onKeyDown={handleKeyDown}
                        name={nameOf<FormFilter>((x) => x.ssn)}
                        className="ease duration-300 transition appearance-none block w-full bg-system-white placeholder-extra-placeholder-text text-secondary-gray-dark font-normal border rounded-xl py-16 text-n px-20 mb-3 leading-tight focus:outline-none focus:bg-white hover:border-active active:border-active focus:border-active"
                        type="text"
                        placeholder="Angiv CPR-nummer"
                        onChange={onChange}
                        value={filterForm.ssn ?? ""}
                    />
                </div>
                <div className="w-full md:w-2/3 mb-3 pl-20">
                    <label
                        className="block tracking-wide text-secondary-gray-dark text-sx font-medium mb-2"
                        htmlFor="grid-name"
                    >
                        Navn
                    </label>
                    <input
                        id="grid-name"
                        autoComplete="name"
                        onKeyDown={handleKeyDown}
                        name={nameOf<FormFilter>((x) => x.name)}
                        className="ease duration-300 transition appearance-none block w-full bg-system-white placeholder-extra-placeholder-text text-secondary-gray-dark font-normal border rounded-xl py-16 text-n px-20 mb-3 leading-tight focus:outline-none focus:bg-white hover:border-active active:border-active focus:border-active"
                        type="text"
                        placeholder="Angiv navn"
                        onChange={onChange}
                        value={filterForm.name ?? ""}
                    />
                </div>
            </div>
            <div
                className={`flex flex-wrap ease duration-300 transition-height ${isOpen ? "max-h-48" : "max-h-0 overflow-hidden"}`}
            >
                {extendedFilters && (
                    <>
                        <div className={`w-full md:w-1/3 mb-[1.5rem]`}>
                            <label
                                className="block tracking-wide text-secondary-gray-dark text-sx font-medium mb-2"
                                htmlFor="grid-municipality"
                            >
                                Kommune
                            </label>
                            <FstyrMultiSelect
                                inputId="grid-municipality"
                                placeholder="Vælg kommune"
                                options={municipalities.map((entry) => ({
                                    value: entry.code,
                                    label: entry.name
                                }))}
                                onOptionClicked={(option) => {
                                    onChangeMunicipality(option.value);
                                }}
                                selectedOptions={filterForm.municipalities}
                            />
                        </div>
                        <div className={`w-full md:w-2/3 mb-6 pl-20`}>
                            <label
                                className="block tracking-wide text-secondary-gray-dark text-sx font-medium mb-2"
                                htmlFor="grid-status"
                            >
                                Status
                            </label>
                            <FstyrMultiSelect
                                inputId={"grid-status"}
                                placeholder="Vælg status"
                                options={statusCodes.map((entry) => ({
                                    value: entry.statusCode,
                                    label: entry.displayValue
                                }))}
                                onOptionClicked={(option) =>
                                    onChangeStatusCode(option.value)
                                }
                                selectedOptions={filterForm.statusTypes}
                            />
                        </div>
                    </>
                )}
                <div className={`w-full md:w-1/3 mb-3 md:mb-0`}>
                    <label
                        className="block tracking-wide text-secondary-gray-dark text-sx font-medium mb-2"
                        htmlFor="grid-period"
                    >
                        Periode
                    </label>
                    <FstyrDateRangePicker
                        id={"grid-period"}
                        onChange={onChangePeriod}
                        value={filterForm.period}
                    />
                </div>
                <div className={`w-full md:w-1/3 mb-3 md:mb-0  pl-20`}>
                    <label
                        className="block tracking-wide text-secondary-gray-dark text-sx font-medium mb-2"
                        htmlFor="grid-errortype"
                    >
                        Fejltype
                    </label>
                    <FstyrMultiSelect
                        inputId="grid-errortype"
                        placeholder="Vælg fejltype"
                        options={[
                            { value: allErrorTypes, label: "Vis alle" },
                            ...errorTypes.map((entry) => ({
                                value: entry.errorCode,
                                label: entry.displayValue
                            }))
                        ]}
                        onOptionClicked={(option) =>
                            onChangeErrorCode(option.value)
                        }
                        selectedOptions={[
                            ...filterForm.errorTypes,
                            ...(filterForm.onlyFormsWithError
                                ? [allErrorTypes]
                                : [])
                        ]}
                    />
                </div>
                <div className={`w-full md:w-1/3 mb-3 md:mb-0 pl-20`}>
                    <label
                        className="block tracking-wide text-secondary-gray-dark text-sx font-medium mb-2"
                        htmlFor="grid-identifier"
                    >
                        ID
                    </label>
                    <input
                        id="grid-identifier"
                        name={nameOf<FormFilter>((x) => x.documentId)}
                        onKeyDown={handleKeyDown}
                        className="ease duration-300 transition appearance-none block w-full bg-system-white placeholder-extra-placeholder-text text-secondary-gray-dark font-normal border rounded-xl py-16 text-n px-20 mb-3 leading-tight focus:outline-none focus:bg-white hover:border-active active:border-active focus:border-active"
                        type="text"
                        value={filterForm.documentId ?? ""}
                        onChange={onChange}
                        placeholder="Angiv ID"
                    />
                </div>
            </div>
            <div className="w-full mt-1">
                <div className="flex justify-between">
                    <div className="flex items-center justify-center">
                        <div
                            className="flex flex-row space-x-4 text-sx font-medium text-primary-identity-Blue justify-center items-center cursor-pointer"
                            onClick={() => setOpenState(!isOpen)}
                        >
                            <ChevronDown
                                className={`transition-transform rotate-${isOpen ? "180" : "0"}`}
                            />
                            <span>
                                {isOpen
                                    ? "Skjul udvidet søgning"
                                    : "Vis udvidet søgning"}
                            </span>
                        </div>
                    </div>
                    <div className="flex">
                        <div className="pr-12">
                            <Button
                                theme="tertiary"
                                onClick={handleFilterReset}
                            >
                                Nulstil
                            </Button>
                        </div>
                        <div>
                            <Button
                                disabled={
                                    componentState !==
                                    FilterComponentState.valid
                                }
                                onClick={handleFormSubmission}
                                iconRight={true}
                            >
                                Søg
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
